import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gift, Sparkles, Truck, MessageCircle } from "lucide-react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { api, imgUrl } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Reveal, MaskReveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { ProductCard } from "../components/ProductCard";
import { QuickView } from "../components/QuickView";

const BENTO_SPANS = ["sm:col-span-2 sm:row-span-2", "", "", "sm:row-span-2", "", "sm:col-span-2"];
const DEFAULT_MARQUEE = ["Vintage Watches", "Gift Hampers", "Fine Jewellery", "Hair Accessories", "Handbags", "Festive Specials", "Combos & Sets"];
const DEFAULT_ORDER = ["hero", "categories", "featured", "marquee", "story", "portfolio", "dispatch"];

export default function Home() {
  const { settings, categories } = useStore();
  const [featured, setFeatured] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [quick, setQuick] = useState(null);
  const [slide, setSlide] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const coverY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);

  useEffect(() => {
    api.get("/products", { params: { limit: 8, sort: "new" } })
      .then((r) => setFeatured(r.data.items)).catch(() => {});
    api.get("/portfolio").then((r) => setPortfolio(r.data.filter((i) => i.show_on_homepage).slice(0, 6))).catch(() => {});
  }, []);

  const banners = [settings?.cover_image_url, ...(settings?.hero_slides || [])].filter(Boolean);
  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % banners.length), 5000);
    return () => clearInterval(t);
  }, [banners.length]);

  const eyebrow = settings?.hero_eyebrow || "Gifts ✦ Accessories ✦ Jewellery";
  const line1 = settings?.hero_line1 || "Thoughtful picks";
  const line2 = settings?.hero_line2 || "for every you";
  const subtext = settings?.hero_subtext || "Vintage watches, delicate jewellery and little luxuries — each piece hand-picked by Afia & Anaya, wrapped like a love letter.";
  const marquee = (settings?.marquee_items || "").split(",").map((s) => s.trim()).filter(Boolean);
  const marqueeItems = marquee.length ? marquee : DEFAULT_MARQUEE;

  const sectionState = (key) => {
    const cfg = Array.isArray(settings?.home_sections) ? settings.home_sections.find((s) => s.key === key) : null;
    return cfg ? !!cfg.visible : true;
  };
  const order = Array.isArray(settings?.home_sections) && settings.home_sections.length
    ? [...settings.home_sections.map((s) => s.key), ...DEFAULT_ORDER.filter((k) => !settings.home_sections.some((s) => s.key === k))]
    : DEFAULT_ORDER;

  const sections = {
    hero: (
      <section key="hero" ref={heroRef} className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 lg:pb-24 lg:pt-16">
          <motion.div style={{ y: textY }} className="relative z-10 max-w-2xl">
            <Reveal delay={0.1}>
              <p className="text-[11px] tracking-[0.4em] text-primary uppercase" data-testid="hero-eyebrow">{eyebrow}</p>
            </Reveal>
            <h1 className="mt-5 font-display text-5xl leading-[1.04] sm:text-6xl lg:text-7xl" data-testid="hero-headline">
              <MaskReveal lines={[line1, line2]} lineClassName="block" />
            </h1>
            <Reveal delay={0.7}>
              <p className="mt-6 max-w-md text-base font-light leading-relaxed text-muted-foreground" data-testid="hero-subtext">{subtext}</p>
            </Reveal>
            <Reveal delay={0.9}>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/shop" data-testid="hero-shop-cta"
                  className="group flex items-center gap-2 rounded-full bg-foreground px-7 py-3.5 text-xs font-medium tracking-[0.2em] text-white uppercase transition-colors hover:bg-primary">
                  Shop the collection
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link to="/about" data-testid="hero-story-cta"
                  className="rounded-full border border-foreground/20 px-7 py-3.5 text-xs font-medium tracking-[0.2em] uppercase transition-colors hover:border-primary hover:text-primary">
                  Our story
                </Link>
              </div>
            </Reveal>
          </motion.div>
          <motion.div style={{ y: coverY }} className="relative z-0 -mt-2 lg:-mt-14 lg:pl-40" data-testid="hero-cover-wrap">
            <div className="relative overflow-hidden rounded-[2rem] shadow-[0_40px_90px_-30px_rgba(176,141,87,0.5)] ring-1 ring-white/70">
              {banners.length ? (
                <div className="relative aspect-[4/3] w-full sm:aspect-[21/9]">
                  <AnimatePresence mode="sync">
                    <motion.img key={banners[slide % banners.length]} src={imgUrl(banners[slide % banners.length])} alt="Anaya — Thoughtful picks for every you"
                      className="absolute inset-0 h-full w-full object-cover" data-testid="hero-cover-image"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.1 }} />
                  </AnimatePresence>
                </div>
              ) : (
                <div className="flex aspect-[21/9] w-full items-center justify-center bg-secondary font-display text-8xl text-primary/40">A</div>
              )}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/10 to-transparent" />
            </div>
            <div className="animate-float absolute -top-5 right-8 hidden rounded-full border border-primary/40 bg-white/70 p-4 backdrop-blur lg:block" style={{ animationDelay: "0.6s" }}>
              <Sparkles className="text-primary" size={22} />
            </div>
            <div className="animate-float glass-deep absolute -bottom-5 left-6 flex items-center gap-3 rounded-2xl px-5 py-3 shadow-lg" style={{ animationDelay: "1.8s" }}>
              <Gift className="text-primary" size={20} />
              <span className="text-xs tracking-widest uppercase">Free gift wrapping</span>
            </div>
            <p className="absolute bottom-4 right-6 hidden text-[10px] tracking-[0.35em] text-white/90 uppercase drop-shadow lg:block">by Afia &amp; Anaya — more than just a store</p>
          </motion.div>
        </div>
      </section>
    ),

    categories: (
      <section key="categories" className="mx-auto max-w-7xl px-4 py-16 sm:px-6" data-testid="home-categories">
        <Reveal>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[11px] tracking-[0.4em] text-primary uppercase">The boutique</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl">Seventeen little worlds</h2>
            </div>
            <Link to="/shop" className="hidden items-center gap-1 text-xs tracking-[0.2em] uppercase hover:text-primary sm:flex" data-testid="categories-view-all">
              View all <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:auto-rows-[9.5rem] sm:grid-cols-4 sm:gap-4">
          {categories.filter((c) => c.is_main).map((c, i) => (
            <Reveal key={c.id} delay={i * 0.05} className={BENTO_SPANS[i % BENTO_SPANS.length]}>
              <Link to={`/category/${c.slug}`} data-testid={`bento-cat-${c.slug}`}
                className="group relative flex h-full min-h-[8.5rem] flex-col justify-end overflow-hidden rounded-3xl border border-white/70 bg-gradient-to-br from-white/80 to-secondary/60 p-5 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(176,141,87,0.45)]">
                {c.image_url && (
                  <img src={imgUrl(c.image_url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-white/85 via-white/20 to-transparent" />
                <span className="relative font-display text-lg leading-tight sm:text-xl">{c.name}</span>
                <span className="relative mt-1 flex items-center gap-1 text-[10px] tracking-[0.25em] text-muted-foreground uppercase transition-colors group-hover:text-primary">
                  Explore <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    ),

    featured: (
      <section key="featured" className="mx-auto max-w-7xl px-4 py-10 sm:px-6" data-testid="home-featured">
        <Reveal>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[11px] tracking-[0.4em] text-primary uppercase">Fresh in</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl">This week&#8217;s picks</h2>
            </div>
            <Link to="/shop" className="flex items-center gap-1 text-xs tracking-[0.2em] uppercase hover:text-primary" data-testid="featured-view-all">
              Shop all <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-4">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} onQuickView={setQuick} />)}
        </div>
      </section>
    ),

    marquee: (
      <section key="marquee" className="mt-16 overflow-hidden border-y border-primary/20 bg-gradient-to-r from-secondary/70 via-white/60 to-accent/50 py-5" data-testid="editorial-marquee">
        <div className="animate-marquee flex w-max whitespace-nowrap">
          {[0, 1].map((half) => (
            <div key={half} className="flex items-center">
              {marqueeItems.map((t, i) => (
                <span key={i} className="mx-8 flex items-center gap-8 font-display text-xl italic text-foreground/70 sm:text-2xl">
                  {t} <span className="text-primary not-italic">&#10022;</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>
    ),

    story: (
      <section key="story" className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="home-story">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="relative mx-auto w-fit">
              <div className="absolute -inset-4 rounded-full border border-primary/30" />
              {settings?.logo_url && (
                <img src={imgUrl(settings.logo_url)} alt="Anaya monogram" className="h-56 w-56 rounded-full object-cover shadow-2xl sm:h-72 sm:w-72" data-testid="story-logo" />
              )}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-[11px] tracking-[0.4em] text-primary uppercase">Our promise</p>
            <h2 className="mt-3 font-display text-3xl leading-snug sm:text-4xl">Curated with love, <em className="gold-text">delivered</em> with care</h2>
            <p className="mt-5 max-w-lg text-base font-light leading-relaxed text-muted-foreground">
              Every Anaya parcel is packed by hand — tissue, ribbon and a note. Whether it&#8217;s a vintage watch for her wrist or a hamper for your best friend, we make gifting feel personal.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {[
                { icon: Gift, label: "Hand-wrapped orders" },
                { icon: Truck, label: "COD across India" },
                { icon: MessageCircle, label: "WhatsApp support" },
              ].map(({ icon: Icon, label }, i) => (
                <div key={i} className="glass rounded-2xl p-4 text-center">
                  <Icon className="mx-auto text-primary" size={20} />
                  <p className="mt-2 text-[11px] tracking-[0.15em] uppercase">{label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    ),

    portfolio: portfolio.length > 0 ? (
      <section key="portfolio" className="mx-auto max-w-7xl px-4 py-16 sm:px-6" data-testid="home-portfolio">
        <Reveal>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[11px] tracking-[0.4em] text-primary uppercase">Portfolio</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl">Moments we&#8217;ve styled</h2>
            </div>
            <Link to="/portfolio" className="flex items-center gap-1 text-xs tracking-[0.2em] uppercase hover:text-primary" data-testid="portfolio-view-all">
              View all <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
          {portfolio.map((it, i) => (
            <Reveal key={it.id} delay={i * 0.07}>
              <Link to="/portfolio" className="group block overflow-hidden rounded-3xl shadow-sm ring-1 ring-white/70" data-testid={`home-portfolio-${it.id}`}>
                <div className="relative overflow-hidden">
                  <img src={imgUrl(it.url)} alt={it.title || "Anaya portfolio"} loading="lazy"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  {it.title && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/60 to-transparent p-4">
                      <p className="font-display text-lg text-white">{it.title}</p>
                      <p className="text-[10px] tracking-[0.25em] text-white/80 uppercase">{it.collection}</p>
                    </div>
                  )}
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    ) : null,

    dispatch: settings?.dispatch_images?.length > 0 ? (
      <section key="dispatch" className="mx-auto max-w-7xl px-4 py-16 sm:px-6" data-testid="dispatch-section">
        <Reveal>
          <div className="text-center">
            <p className="text-[11px] tracking-[0.4em] text-primary uppercase">From our hands to yours</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl">Your orders, packed with love</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm font-light leading-relaxed text-muted-foreground">
              Every order is carefully packed and prepared with love before dispatch. Thank you for choosing Anaya Fashion &amp; Gifts!
            </p>
          </div>
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-3" data-testid="dispatch-gallery">
          {settings.dispatch_images.map((u, i) => (
            <Reveal key={u} delay={i * 0.1}>
              <div className="overflow-hidden rounded-3xl shadow-[0_20px_50px_-20px_rgba(61,43,51,0.35)] ring-1 ring-white/70">
                <img src={imgUrl(u)} alt={`Order packed for dispatch ${i + 1}`} loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" data-testid={`dispatch-img-${i}`} />
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    ) : null,
  };

  return (
    <div data-testid="home-page">
      <Seo title="Anaya Fashion & Gifts — Watches, Jewellery & Gift Hampers" description="Shop vintage watches, jewellery, gift hampers, perfumes and accessories at Anaya Fashion & Gifts. Hand-wrapped parcels, COD across India, WhatsApp support." path="/" image="/og-image.jpg" />
      {order.filter((k) => sections[k] && sectionState(k)).map((k) => sections[k])}
      <QuickView product={quick} onClose={() => setQuick(null)} />
    </div>
  );
}
